import React from "react";
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Progress, Row, Spinner } from "reactstrap";
import type { DiagnosticoSincronizacion } from "../types";
import "./SyncModal.css";

const EXPECTED_ENDPOINTS = [
  "compras/estado",
  "recetas/sincronizar",
  "compras/sincronizar-rango",
  "costo-promedio/sincronizar",
  "ventas/comanda/sincronizar",
  "ventas/comanda/aplicar-costos",
  "ventas/movimientos/sincronizar",
  "ventas/movimientos/aplicar-costos-comanda",
  "stock/remisiones/sincronizar",
  "stock/mermas/sincronizar",
  "stock/inventarios/sincronizar",
] as const;

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("es-PY");
};

const formatDuration = (inicio?: string | null, fin?: string | null) => {
  if (!inicio || !fin) return null;
  const ms = new Date(fin).getTime() - new Date(inicio).getTime();
  if (isNaN(ms) || ms < 0) return null;
  const seg = Math.floor(ms / 1000);
  if (seg < 60) return `${seg}s`;
  const min = Math.floor(seg / 60);
  const remSeg = seg % 60;
  return remSeg > 0 ? `${min}m ${remSeg}s` : `${min}m`;
};

type SyncModalProps = {
  isOpen: boolean;
  onToggle: () => void;
  onStartSync: (days: 4 | 7 | 15 | 20 | 31) => void;
  isStarting: boolean;
  startingDays: number | null;
  diagnostico: DiagnosticoSincronizacion | null;
  isLoading: boolean;
  loadError: string | null;
  actionError: string | null;
};

const SyncModal = ({
  isOpen,
  onToggle,
  onStartSync,
  isStarting,
  startingDays,
  diagnostico,
  isLoading,
  loadError,
  actionError,
}: SyncModalProps) => {
  const status = diagnostico?.estado ?? null;
  const ultimo = diagnostico?.ultimo ?? null;
  const endpoints = diagnostico?.endpoints ?? null;
  const hasDiagnostico = Boolean(diagnostico);
  const canUseOptions =
    Boolean(endpoints?.ok) && !isStarting && !status?.enProgreso;
  const endpointsOk = endpoints?.ok ?? null;
  const endpointsError = endpoints?.error ?? null;
  const endpointByName = new Map(
    (endpoints?.resultados ?? []).map((item) => [item.name, item]),
  );
  const endpointRows = EXPECTED_ENDPOINTS.map((name) => endpointByName.get(name));

  const today = new Date();
  const formatDate = (value: Date) => value.toLocaleDateString("es-PY");
  const formatRange = (days: number) => {
    const end = new Date(today);
    const start = new Date(today);
    start.setDate(start.getDate() - (days - 1));
    return { start: formatDate(start), end: formatDate(end) };
  };

  const ultimoEstado = ultimo?.estado ?? null;
  const ultimoEsError = ultimoEstado === "error";
  const ultimoEsOk = ultimoEstado === "ok";
  const duracion = formatDuration(ultimo?.inicio, ultimo?.fin);

  return (
    <Modal
      isOpen={isOpen}
      toggle={onToggle}
      centered
      className="sync-modal-wide"
    >
      <ModalHeader toggle={onToggle}>Sincronizar datos</ModalHeader>
      <ModalBody>
        <Row className="g-3 mb-3">

          {/* Estado actual */}
          <Col lg={4}>
            <div className="fw-semibold mb-1">Estado actual</div>
            {isLoading && !hasDiagnostico ? (
              <div className="d-flex align-items-center gap-2 text-muted mt-2">
                <Spinner size="sm" />
                <span>Cargando...</span>
              </div>
            ) : loadError ? (
              <div className="text-danger small mt-1">{loadError}</div>
            ) : (
              <div className="small text-muted">
                <div>
                  <span className={status?.enProgreso ? "text-warning fw-semibold" : "text-secondary"}>
                    {status?.enProgreso ? "⏳ En progreso" : "✓ Inactivo"}
                  </span>
                </div>
                {status?.enProgreso && (
                  <div className="mt-1">
                    <Progress
                      value={status?.progreso ?? 0}
                      color="primary"
                      style={{ height: "6px" }}
                      className="mb-1"
                    />
                    <div className="d-flex justify-content-between">
                      <span>{status?.progreso ?? 0}%</span>
                      <span>Paso {status?.pasoActual ?? 0}/{status?.pasosTotales ?? 0}</span>
                    </div>
                  </div>
                )}
                <div className="mt-1">{status?.comentario ?? "-"}</div>
                <div>Inicio: {formatDateTime(status?.inicioIso)}</div>
                {status?.finIso && <div>Fin: {formatDateTime(status.finIso)}</div>}
                <div>Rango: {status?.desde ?? "-"} → {status?.hasta ?? "-"}</div>
                <div>Servidor: {status?.server ?? "-"}</div>
                {status?.usuarioNombre && <div>Usuario: {status.usuarioNombre}</div>}
                {status?.enProgreso && (
                  <div className="text-warning mt-1 small">
                    Esperá a que finalice antes de iniciar otra.
                  </div>
                )}
              </div>
            )}
          </Col>

          {/* Última sincronización */}
          <Col lg={4}>
            <div className="fw-semibold mb-1">Última sincronización</div>
            {ultimo ? (
              <div className="small">
                <div className="mb-1">
                  <span className={`badge ${ultimoEsError ? "bg-danger" : ultimoEsOk ? "bg-success" : "bg-secondary"}`}>
                    {ultimoEstado?.toUpperCase() ?? "?"}
                  </span>
                  {duracion && <span className="ms-2 text-muted">{duracion}</span>}
                </div>
                <div className="text-muted">
                  <div>Inicio: {formatDateTime(ultimo.inicio)}</div>
                  {ultimo.fin && <div>Fin: {formatDateTime(ultimo.fin)}</div>}
                  <div>Rango: {ultimo.desde ?? "-"} → {ultimo.hasta ?? "-"}</div>
                  <div>Servidor: {ultimo.server ?? "-"}</div>
                  {ultimo.usuario_nombre && <div>Usuario: {ultimo.usuario_nombre}</div>}
                </div>
                {ultimo.comentario && (
                  <div className={`mt-1 ${ultimoEsError ? "text-danger" : "text-muted"}`}>
                    {ultimo.comentario}
                  </div>
                )}
                {ultimoEsError && ultimo.error && ultimo.error !== ultimo.comentario && (
                  <div className="mt-1">
                    <div className="text-danger fw-semibold small">Detalle del error:</div>
                    <pre
                      className="text-danger small mb-0"
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        background: "#fff5f5",
                        border: "1px solid #f5c6cb",
                        borderRadius: "4px",
                        padding: "4px 6px",
                        maxHeight: "80px",
                        overflowY: "auto",
                      }}
                    >
                      {ultimo.error}
                    </pre>
                  </div>
                )}
                {(() => {
                  const resumen = ultimo?.resumen as Record<string, { ok?: boolean; fuentesConError?: { nombre: string; error: string }[] }> | null | undefined;
                  if (!resumen) return null;
                  const pasos = Object.entries(resumen).flatMap(([paso, val]) =>
                    Array.isArray(val?.fuentesConError) && val.fuentesConError.length > 0
                      ? val.fuentesConError.map((e) => ({ paso, ...e }))
                      : [],
                  );
                  if (pasos.length === 0) return null;
                  return (
                    <div className="mt-1">
                      <div className="text-warning fw-semibold small">Fuentes con error en último sync:</div>
                      {pasos.map((e, i) => (
                        <div key={i} className="text-warning small" style={{ wordBreak: "break-word" }}>
                          [{e.paso}] {e.nombre}: {e.error}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="small text-muted mt-1">Sin historial todavía.</div>
            )}
          </Col>

          {/* Verificación de endpoints */}
          <Col lg={4}>
            <div className="fw-semibold mb-1">Verificación de endpoints</div>
            {isLoading && !hasDiagnostico ? (
              <div className="d-flex align-items-center gap-2 text-muted mt-2">
                <Spinner size="sm" />
                <span>Probando endpoints...</span>
              </div>
            ) : endpointsOk ? (
              <div className="text-success small mb-1">✓ Listo para sincronizar</div>
            ) : (
              <div className="text-warning small mb-1">
                {endpointsError || "Aún no se validaron los endpoints."}
              </div>
            )}
            {endpointRows.length > 0 && (
              <div className="small" style={{ fontFamily: "monospace", fontSize: "0.72rem" }}>
                {endpointRows.map((item, index) => {
                  const ok = item?.ok;
                  const pending = !item;
                  return (
                    <div key={EXPECTED_ENDPOINTS[index]} className={ok ? "text-success" : pending ? "text-muted" : "text-danger"}>
                      [{ok ? "OK" : pending ? "··" : "ER"}] {EXPECTED_ENDPOINTS[index]}
                      {item && !item.ok && item.error ? (
                        <span className="text-danger"> · {item.error}</span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </Col>
        </Row>

        {/* Selector de rango */}
        <div className="fw-semibold mb-2">Selecciona rango</div>
        <div className="d-flex gap-3 flex-wrap">
          {[4, 7, 15, 20, 31].map((days) => (
            <div key={days} className="d-flex flex-column align-items-start">
              <Button
                color="primary"
                outline
                disabled={!canUseOptions}
                onClick={() => onStartSync(days as 4 | 7 | 15 | 20 | 31)}
              >
                {isStarting && startingDays === days ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Ejecutando...
                  </>
                ) : (
                  `${days} días`
                )}
              </Button>
              <span className="small text-muted mt-1">
                {formatRange(days).start}
                <br />
                {formatRange(days).end}
              </span>
            </div>
          ))}
        </div>
        {actionError && (
          <div className="mt-2">
            <div className="text-danger small fw-semibold">Error al iniciar:</div>
            <pre
              className="text-danger small mb-0"
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                background: "#fff5f5",
                border: "1px solid #f5c6cb",
                borderRadius: "4px",
                padding: "4px 6px",
                maxHeight: "80px",
                overflowY: "auto",
              }}
            >
              {actionError}
            </pre>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onToggle} disabled={isStarting}>
          Cerrar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SyncModal;
